import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { MessageCircle } from "lucide-react";

import { SupportWhatsAppLink } from "./SupportWhatsAppLink";
import {
  FLOATING_BUTTONS_HIDDEN_CLASS,
  FLOATING_BUTTONS_TRANSITION
} from "./useFloatingButtonsLift";

type Props = {
  profile: SalonStockerHttpTypes.StoreProfile;
};

/**
 * Right padding a control that sits in the same bottom-right corner as this FAB
 * without a bottom bar to lift it - such as the cart rail's Save as list button
 * on a desktop - needs so its trailing edge clears the FAB. The button sits
 * `right-2` + `w-11` (8+44 = 52px) from a phone's right edge and `sm:right-6`
 * (24+44 = 68px) from `sm`; `pr-[60px]`/`sm:pr-[76px]` leave a snug ~8px gap so
 * the button and the FAB read as a pair. Bottom action bars do not use this:
 * they lift the FAB above them through `useFloatingButtonsLift`. Kept here,
 * beside the geometry it is derived from, so the two move together.
 */
export const SUPPORT_FAB_BAR_CLEARANCE = "pr-[60px] sm:pr-[76px]";

/**
 * The persistent WhatsApp contact/help FAB (KAN-13), mounted once in
 * {@link MainLayout} so it floats over every authenticated page. Brand green,
 * bottom-right, sized (h-11) to sit level with the checkout action button it
 * shares the corner with on the cart and checkout bottom bars.
 *
 * A bottom action bar never sits under it: a bar in this corner publishes its
 * distance from the viewport bottom through `useFloatingButtonsLift` and the
 * FAB rides above it (`--floating-buttons-lift`, `0px` when there is none). The
 * `bottom` offset below is the gap left above the bar. The corner geometry is
 * mirrored in that hook, so change them together.
 */
export function WhatsAppSupportButton({ profile }: Props) {
  return (
    <SupportWhatsAppLink
      profile={profile}
      aria-label="Chat with us on WhatsApp"
      title="Chat with us on WhatsApp"
      className={cn(
        "fixed bottom-[calc(0.75rem+var(--floating-buttons-lift,0px))] right-2 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg duration-300 hover:scale-110 hover:bg-[#20bd5a] hover:shadow-xl sm:right-6",
        FLOATING_BUTTONS_TRANSITION,
        FLOATING_BUTTONS_HIDDEN_CLASS
      )}
    >
      <MessageCircle className="h-6 w-6" />
    </SupportWhatsAppLink>
  );
}
